import React, { FC } from 'react';
import { Divider } from 'wix-ui-tpa/Divider';
import { useEnvironment, WidgetProps } from '@wix/yoshi-flow-editor';

import { ControllerProps, DataHook } from '../../../types';
import Section from './common/Section';
import Header from './Header';
import Description from './Description';
import Badges from './Badges';
import Overview from './Overview';
import BlogPosts from './BlogPosts';
import EmptyState from './EmptyState';
import {
  useHeaderRenderer,
  useDescriptionRenderer,
  useOverviewRenderer,
  useBadgesRenderer,
  useBlogPostsRendered,
  useEmptyStateRenderer,
  useBlockedEmptyStateRenderer,
} from './hooks';
import { classes, st } from './Widget.st.css';

const getInfoSectionContentState = (
  shouldDisplayOverviewAsSidebar: boolean,
  shouldRenderOverview: boolean,
) => ({
  fullWidth: !shouldDisplayOverviewAsSidebar || !shouldRenderOverview,
});

const getInfoSectionSidebarState = (
  shouldDisplayOverviewAsSidebar: boolean,
  shouldRenderInfoSectionContent: boolean,
) => ({
  fullWidth: !shouldRenderInfoSectionContent || !shouldDisplayOverviewAsSidebar,
});

export const Widget: FC<WidgetProps<ControllerProps>> = ({ host }) => {
  const { shouldRenderHeader } = useHeaderRenderer();
  const { shouldRenderDescription } = useDescriptionRenderer();
  const { shouldRenderBadges } = useBadgesRenderer();
  const { shouldRenderOverview } = useOverviewRenderer();
  const { shouldRenderBlogPosts } = useBlogPostsRendered();
  const { shouldRenderEmptyState } = useEmptyStateRenderer({
    shouldRenderDescription,
    shouldRenderBadges,
    shouldRenderOverview,
    shouldRenderBlogPosts,
  });
  const { shouldRenderBlockedEmptyState } = useBlockedEmptyStateRenderer();
  const { isMobile, isRTL, isEditorX } = useEnvironment();

  const shouldRenderInfoSectionContent =
    shouldRenderDescription || shouldRenderBadges;

  const shouldDisplayOverviewAsSidebar = host.dimensions.width
    ? host.dimensions.width > 750
    : true;

  return (
    <div
      className={st(classes.root, {
        mobile: isMobile,
        responsive: !host.dimensions.width,
      })}
      dir={isRTL ? 'rtl' : 'ltr'}
      data-hook={DataHook.WidgetWrapper}
    >
      {shouldRenderHeader && <Header />}
      {!isMobile && <Divider className={classes.divider} />}
      {shouldRenderBlockedEmptyState || shouldRenderEmptyState ? (
        <EmptyState isBlocked={shouldRenderBlockedEmptyState} />
      ) : (
        <>
          <div
            className={st(classes.infoSectionsWrapper, {
              childrenFullWidth: !shouldDisplayOverviewAsSidebar,
              responsive: isEditorX,
            })}
          >
            {shouldRenderInfoSectionContent && (
              <div
                className={st(
                  classes.infoSectionContent,
                  getInfoSectionContentState(
                    shouldDisplayOverviewAsSidebar,
                    shouldRenderOverview,
                  ),
                )}
              >
                {shouldRenderDescription && (
                  <Section>
                    <Description />
                  </Section>
                )}
                {shouldRenderBadges && (
                  <Section>
                    <Badges />
                  </Section>
                )}
              </div>
            )}
            {shouldRenderOverview && (
              <Section
                className={st(
                  classes.infoSectionSidebar,
                  getInfoSectionSidebarState(
                    shouldDisplayOverviewAsSidebar,
                    shouldRenderInfoSectionContent,
                  ),
                )}
              >
                <Overview />
              </Section>
            )}
          </div>
          {shouldRenderBlogPosts && (
            <Section>
              <BlogPosts />
            </Section>
          )}
        </>
      )}
    </div>
  );
};
